import { useCallback, useMemo } from "react";
import type { WizardSectorStep } from "@/types/risk-wizard";

type UseWizardNavigationProps = {
  currentMainStep: number;
  currentSectorIndex: number;
  sectorSteps: WizardSectorStep[];
  isSaving: boolean;
  canProceed: boolean;
  isCurrentSectorCompleted: boolean;
  setCurrentMainStep: (step: number) => void;
  setCurrentSectorIndex: (index: number) => void;
  onSkipSector?: (sectorId: string) => void;
  onComplete?: () => void;
};

type UseWizardNavigationReturn = {
  canGoBack: boolean;
  canGoNext: boolean;
  isLastSector: boolean;
  goBack: () => void;
  goNext: () => void;
  goToSector: (sectorId: string) => void;
  skipSector: () => void;
  navigateToSectorIndex: (targetIndex: number) => void;
};

export function useWizardNavigation({
  currentMainStep,
  currentSectorIndex,
  sectorSteps,
  isSaving,
  canProceed,
  isCurrentSectorCompleted,
  setCurrentMainStep,
  setCurrentSectorIndex,
  onSkipSector,
  onComplete,
}: UseWizardNavigationProps): UseWizardNavigationReturn {
  const canGoBack = useMemo(() => currentMainStep > 1, [currentMainStep]);

  const canGoNext = useMemo(() => {
    if (isSaving) return false;
    return canProceed;
  }, [isSaving, canProceed]);

  const isLastSector = useMemo(
    () =>
      currentMainStep === 2 && currentSectorIndex === sectorSteps.length - 1,
    [currentMainStep, currentSectorIndex, sectorSteps.length]
  );

  const goBack = useCallback(() => {
    if (currentMainStep === 2 && currentSectorIndex > 0) {
      setCurrentSectorIndex(currentSectorIndex - 1);
    } else if (currentMainStep === 2 && currentSectorIndex === 0) {
      setCurrentMainStep(1);
    } else if (currentMainStep > 1) {
      setCurrentMainStep(currentMainStep - 1);
    }
  }, [
    currentMainStep,
    currentSectorIndex,
    setCurrentMainStep,
    setCurrentSectorIndex,
  ]);

  const goNext = useCallback(() => {
    if (currentMainStep === 1) {
      setCurrentMainStep(2);
    } else if (currentMainStep === 2) {
      if (currentSectorIndex < sectorSteps.length - 1) {
        setCurrentSectorIndex(currentSectorIndex + 1);
      } else {
        setCurrentMainStep(3);
      }
    } else if (currentMainStep === 3) {
      setCurrentMainStep(4);
    } else if (currentMainStep === 4) {
      onComplete?.();
    }
  }, [
    currentMainStep,
    currentSectorIndex,
    sectorSteps.length,
    setCurrentMainStep,
    setCurrentSectorIndex,
    onComplete,
  ]);

  const goToSector = useCallback(
    (sectorId: string) => {
      const index = sectorSteps.findIndex((s) => s.sectorId === sectorId);
      if (index === -1) return;

      setCurrentMainStep(2);
      setCurrentSectorIndex(index);
    },
    [sectorSteps, setCurrentMainStep, setCurrentSectorIndex]
  );

  const skipSector = useCallback(() => {
    const currentSector = sectorSteps[currentSectorIndex];
    if (!currentSector) return;

    onSkipSector?.(currentSector.sectorId);

    if (currentSectorIndex < sectorSteps.length - 1) {
      setCurrentSectorIndex(currentSectorIndex + 1);
    } else {
      setCurrentMainStep(3);
    }
  }, [
    currentSectorIndex,
    sectorSteps,
    onSkipSector,
    setCurrentSectorIndex,
    setCurrentMainStep,
  ]);

  const navigateToSectorIndex = useCallback(
    (targetIndex: number) => {
      if (targetIndex === currentSectorIndex) return;
      if (targetIndex < 0 || targetIndex >= sectorSteps.length) return;

      const isForward = targetIndex > currentSectorIndex;
      const currentSector = sectorSteps[currentSectorIndex];

      if (isForward && currentSector && !isCurrentSectorCompleted) {
        onSkipSector?.(currentSector.sectorId);
      }

      setCurrentSectorIndex(targetIndex);
    },
    [
      currentSectorIndex,
      sectorSteps,
      isCurrentSectorCompleted,
      onSkipSector,
      setCurrentSectorIndex,
    ]
  );

  return {
    canGoBack,
    canGoNext,
    isLastSector,
    goBack,
    goNext,
    goToSector,
    skipSector,
    navigateToSectorIndex,
  };
}
